'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Meridian Transit primitives shared by the Guides pages (how-it-works,
 * requirements, help, faq): a 160px coordinate rail that runs down the left
 * of editorial content, and constellation-style data rows that replace
 * bordered tables. Kept in one file so the rhythm — node size, rule weight,
 * mono tracking — can't drift between pages.
 */

/** Small mono coordinate readout: a muted node + an uppercase tracking label. Used inline on mobile where the rail collapses, and standalone on task-shaped banners. */
export function CoordinateTag({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground', className)}>
      <span aria-hidden className="size-1.5 rounded-full bg-muted-foreground/70" />
      {children}
    </span>
  )
}

/**
 * One stretch of the transit: a 160px rail column (node + coordinate, lg+
 * only) beside a content column. Below lg the rail collapses and the
 * coordinate renders inline above the heading instead of disappearing.
 */
export function RailSection({
  id,
  coordinate,
  eyebrow,
  title,
  lede,
  children,
  className,
}: {
  id?: string
  coordinate: string
  eyebrow?: string
  title?: ReactNode
  lede?: ReactNode
  children?: ReactNode
  className?: string
}) {
  return (
    <section id={id} className={cn('relative grid grid-cols-1 gap-6 lg:grid-cols-[160px_1fr] lg:gap-10', className)}>
      <div aria-hidden className="relative hidden lg:block">
        <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-foreground/10" />
        <div className="sticky top-28 flex flex-col items-center gap-2 px-2 text-center">
          <span className="relative z-10 size-1.5 rounded-full bg-muted-foreground ring-4 ring-background" />
          {eyebrow ? (
            <span className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-foreground">{eyebrow}</span>
          ) : null}
          <span className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.14em] text-muted-foreground">
            {coordinate}
          </span>
        </div>
      </div>

      <div className="min-w-0">
        <div className="mb-3 lg:hidden">
          <CoordinateTag>
            {eyebrow ? `${eyebrow} · ` : ''}
            {coordinate}
          </CoordinateTag>
        </div>
        {title ? <h2 className="text-2xl font-bold text-foreground md:text-3xl">{title}</h2> : null}
        {lede ? <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">{lede}</p> : null}
        {children ? <div className={cn(title || lede ? 'mt-8' : undefined)}>{children}</div> : null}
      </div>
    </section>
  )
}

/** Boundary mark between two RailSections: a short tick on the meridian and the degree it has turned through. Never glows — only the primary CTA does. */
export function MeridianTick({ degree }: { degree: string }) {
  return (
    <div aria-hidden className="relative hidden h-10 lg:grid lg:grid-cols-[160px_1fr] lg:gap-10">
      <div className="relative">
        <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-foreground/10" />
        <span className="absolute left-1/2 top-1/2 h-px w-3 -translate-x-1/2 -translate-y-1/2 bg-foreground/30" />
        <span className="absolute left-[calc(50%+10px)] top-1/2 -translate-y-1/2 whitespace-nowrap font-mono text-[10px] tracking-wide text-muted-foreground">
          {degree}
        </span>
      </div>
      <div />
    </div>
  )
}

/** A label/value pair joined by a dotted leader ending in a node — stands in for a bordered table row. */
export function ConstellationRow({
  label,
  value,
  caption,
  className,
}: {
  label: ReactNode
  value: ReactNode
  caption?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex items-end gap-3 py-2.5', className)}>
      <div className="min-w-0 max-w-[60%] shrink-0">
        <div className="truncate text-sm text-foreground">{label}</div>
        {caption ? <div className="mt-0.5 truncate text-xs text-muted-foreground">{caption}</div> : null}
      </div>
      <span aria-hidden className="leader-dots h-px flex-1 -translate-y-[5px]" />
      <div className="flex shrink-0 items-center gap-2">
        <span className="whitespace-nowrap text-sm font-medium tabular-nums text-foreground">{value}</span>
        <span aria-hidden className="size-1 rounded-full bg-muted-foreground/60" />
      </div>
    </div>
  )
}

/**
 * Live "current position" readout for an ordered run of sections: an
 * IntersectionObserver tracks which section is centred in the viewport and
 * a fixed dock reports that section's coordinate, with a trail of dots for
 * the stretches already crossed. A transit log, not a progress bar — it
 * reports position, it doesn't push completion.
 */
export function TransitLog({ stops }: { stops: { id: string; coordinate: string; label: string }[] }) {
  const [activeIndex, setActiveIndex] = useState(0)
  const seen = useRef<Set<number>>(new Set([0]))
  const [visited, setVisited] = useState<number[]>([0])

  useEffect(() => {
    const elements = stops
      .map((stop, index) => ({ index, el: document.getElementById(stop.id) }))
      .filter((entry): entry is { index: number; el: HTMLElement } => Boolean(entry.el))

    if (elements.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const match = elements.find((item) => item.el === entry.target)
          if (!match) continue
          setActiveIndex(match.index)
          if (!seen.current.has(match.index)) {
            seen.current.add(match.index)
            setVisited(Array.from(seen.current))
          }
        }
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    )

    elements.forEach((item) => observer.observe(item.el))
    return () => observer.disconnect()
  }, [stops])

  const current = stops[activeIndex]
  if (!current) return null

  return (
    <div aria-live="polite" className="pointer-events-none fixed bottom-5 left-1/2 z-30 hidden -translate-x-1/2 lg:block">
      <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-border bg-card/90 px-4 py-2 shadow-lg shadow-black/20 backdrop-blur">
        <span aria-hidden className="flex items-center gap-1">
          {stops.map((stop, index) => (
            <span
              key={stop.id}
              className={cn(
                'size-1.5 rounded-full transition-colors',
                index === activeIndex ? 'bg-primary' : visited.includes(index) ? 'bg-muted-foreground' : 'bg-foreground/15'
              )}
            />
          ))}
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{current.coordinate}</span>
        <span className="hidden text-sm font-medium text-foreground sm:inline">{current.label}</span>
      </div>
    </div>
  )
}
